import React, { CSSProperties, PureComponent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { throttle } from 'lodash-es';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import { addResize, ResizeProps } from 'decorators/addResize';
import { RootState, StoreProps } from 'store';
import { saveUI } from 'actions/ui';
import getConfigurationByName from 'selectors/getConfigurationByName';
import getUIByName from 'selectors/getUIByName';
import ItemConfiguration from 'types/ItemConfiguration';
import { AnyObject } from 'types/Common';
import {
  DOUBLE_CLICK_DELAY,
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from 'constants/ui';
import tableNames from 'constants/tableNames';
import { isEmpty, parseColumnsWidthsFromHeader } from './helpers';
import Animation from 'components/ui/animation';
import { Column, Row } from 'components/ui/table';
import Loader from 'components/ui/loader';
import NoContent from 'components/ui/noContent';
import SmartList from 'components/ui/smartList';
import TableHeaders from 'components/tableHeaders';
import './table.scss';

interface OwnProps {
  tableName: tableNames;
  items: AnyObject[];
  totalItems?: number;
  isFetch?: boolean;
  isListView?: boolean;
  resizableColumns?: boolean;
  canViewItem?: boolean;
  customClass?: string;
  headerRender?: React.ReactNode;
  noContentText?: string;
  outerSort?: {
    field: string;
    order: string;
  };
  rowHeight?: number;
  headerHeight?: number;
  minColumnsWidth?: { [key: string]: number };
  forbidResizeFor?: string[];
  customConfiguration?: { id: string }[];
  renderMaxHeight?: number;
  loadNextPage?: () => Promise<void>;
  rowRender?: (data: any, index: number) => React.ReactNode;
  clickToItem?: (data) => void;
  onSort?: (data: { field: string; order: string }) => void;
  getTableRef?: (ref: HTMLElement) => void;
}

interface ConnectedProps {
  configuration: ItemConfiguration[];
  ui: any;
}

type Props = OwnProps & ConnectedProps & StoreProps & IntlProps & ResizeProps;

interface State {
  tableHeight: CSSProperties['height'];
  showTable: boolean;
  rowClicked?: {
    id: number;
    count: number;
  };
}

const SEARCH_BLOCK_HEIGHT = 83;
@addResize
class DynamicTable extends PureComponent<Props, State> {
  scrollbarRef: HTMLElement | null = null;
  headerRef: HTMLElement | null = null;
  tableRef: HTMLElement | null = null;
  doubleClickTimer: NodeJS.Timeout | undefined = undefined;
  isTableReopened: number;

  constructor(props: Props) {
    super(props);

    this.state = {
      tableHeight: 0,
      showTable: false,
    };

    this.calcTableHeight = throttle(this.calcTableHeight, 300);
    this.isTableReopened = props.items.length;
  }

  componentDidMount() {
    this.calcTableHeight();
    this.forceUpdate();
    this.attachEvents();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { configuration: currConfig, tableName } = this.props;
    const { configuration: prevConfig } = prevProps;

    if (
      prevConfig[0] &&
      currConfig[0] &&
      prevConfig[0].fixed !== currConfig[0].fixed
    ) {
      this.reloadHorizontalScroll();
    }

    const itemsBefore = prevProps.items.length;
    const itemsNow = this.props.items.length;

    if (itemsBefore !== itemsNow) {
      this.calcTableHeight();
    }

    if (
      this.props.resizableColumns &&
      prevConfig.length &&
      currConfig.length !== prevConfig.length &&
      prevProps.tableName === tableName
    ) {
      this.handleConfigurationChange();
    }
  }

  componentWillUnmount() {
    const { dispatch, tableName } = this.props;

    // This condition is necessary when the table is empty
    if (this.scrollbarRef) {
      dispatch(
        saveUI(tableName, {
          scrollTop: this.scrollbarRef.scrollTop,
          scrollLeft: this.scrollbarRef.scrollLeft,
        })
      );
    }

    this.detachEvents();
  }

  attachEvents = () => {
    window.addEventListener('resize', this.calcTableHeight);
  };

  detachEvents = () => {
    window.removeEventListener('resize', this.calcTableHeight);
  };

  renderRowWithProps = (data: any, index: number) => {
    const { rowRender, width, forbidResizeFor } = this.props;
    const renderer = rowRender || this.rowRender;

    return renderer(
      {
        ...data,
        columnWidths: width,
        forbidResizeFor,
        handleDoubleClick: this.handleDoubleRowClick(data, index),
      },
      index
    );
  };

  handleDoubleRowClick = (data, rowIndex) => (onItemClick: any) => {
    const { rowClicked } = this.state;
    const { count = 0, id } = rowClicked || {};

    this.setState({
      rowClicked: { id: rowIndex, count: id !== rowIndex ? 1 : count + 1 },
    });

    if (!count || id !== rowIndex) {
      clearTimeout(this.doubleClickTimer);
    }

    this.doubleClickTimer = setTimeout(() => {
      const selectedArea = document.getSelection();
      const selectedText = selectedArea && Boolean(selectedArea.toString());

      if (selectedText) {
        return;
      }

      onItemClick && onItemClick(data);
    }, DOUBLE_CLICK_DELAY);
  };

  rowRender = ({ handleDoubleClick, ...data }) => {
    const { configuration, clickToItem, canViewItem = true } = this.props;
    const { columnWidths, ...columnsData } = data;

    let rowId = '';
    if (data.projectId && data.transactionId) {
      rowId = `data-${data.projectId}-${data.transactionId}`;
    }

    return (
      <Row
        id={rowId}
        onClick={() => handleDoubleClick(clickToItem)}
        customClass={classNames({
          'ui-table__row_readonly': !canViewItem,
          'ui-table__row_clickable': clickToItem,
        })}
        needAnimate={data.needAnimate}
        status={data.status || data.paymentStatus}>
        {configuration.map((config) => {
          const { id } = config;
          return (
            <Column
              columnWidths={columnWidths}
              id={id}
              modifier={id}
              key={`${data[id]}_${id}`}
              customClass={classNames({
                'ui-table__column_fixed': config.fixed,
              })}
              params={{
                ...config,
                valueId: id,
                valueType: config.valueType,
              }}
              content={data[id]}
              data={columnsData}
            />
          );
        })}
      </Row>
    );
  };

  getCustomContentScrollWidth = () => {
    const { width, configuration } = this.props;
    let contentWidth = 0;

    if (width) {
      configuration.forEach((item) => {
        if (item.active) {
          contentWidth += Number(width[item.id]) || 0;
        }
      });
    }

    return contentWidth;
  };

  setTopPositionToCustomHeader = (header) => {
    const { tableHeight } = this.state;
    const {
      resizableColumns,
      updateWidth,
      width,
      forbidResizeFor,
      minColumnsWidth,
      headerHeight = TABLE_HEADER_HEIGHT,
    } = this.props;

    if (header) {
      const props: any = {
        style: { ...header.props?.style },
      };

      if (resizableColumns) {
        props.childrenProps = {
          columnWidths: width,
          updateWidth,
          forbidResizeFor,
          minColumnsWidth,
          tableHeight,
          headerHeight,
        };
        props.setHeaderRef = this.setHeaderRef;
      }

      return React.cloneElement(header, props);
    }

    return null;
  };

  calcTableHeight = () => {
    const {
      items,
      isListView,
      renderMaxHeight,
      rowHeight = TABLE_ROW_HEIGHT,
      headerHeight = TABLE_HEADER_HEIGHT,
    } = this.props;

    if (!this.tableRef) return;

    if (items.length === 0) {
      this.setState({
        tableHeight: 'auto',
      });
      return;
    }

    let total = 0;
    let maxHeight = 0;
    let horizontalScrollHeight = 0;
    const paddingBottom = 16;
    const scrollRef = this.tableRef.closest('.ui-scrollbar');

    if (scrollRef?.querySelector('.trackXVisible')) {
      horizontalScrollHeight = 8;
    }

    const height =
      rowHeight * items.length + headerHeight + horizontalScrollHeight;

    // Такая высота необходима когда нет данных и выводится NoContent
    if (isListView) {
      total = height;
    } else {
      if (renderMaxHeight) {
        // для ручного выставления максимум длины таблицы
        maxHeight = renderMaxHeight;
      } else {
        const offset = this.tableRef.getBoundingClientRect();
        maxHeight = window.innerHeight - offset.top - paddingBottom;

        // Дополнительные расчеты спрятанного блока необходимы при переключении между страницами
        // и открытым блоком поиска
        if (this.isTableReopened) {
          maxHeight += SEARCH_BLOCK_HEIGHT;
        }
      }

      total = height > maxHeight && maxHeight > 0 ? maxHeight : height;
    }

    this.setState({
      tableHeight: total,
    });
  };

  /***
   * При переключении режима просмотра таблицы (фиксированная первая колонка),
   * мы изменяем размеры и позицию области, в которой работает скролл,
   * поэтому нужно пересчитать размеры для элементов track и thumb.
   * Один из способов это сделать - изменить положение скролла, с помощью
   * scrollLeft.
   * Особенность в том, что пересчет произойдет, только если новое положение
   * scrollLeft отличное от текушего, поэтому нужно сетить 0 или 1.
   * Например, если переключиться из обычного режима со scrollLeft = 0
   * в фиксированный, то повторный сеттинг scrollLeft = 0, ничего не даст и
   * размеры будут кривые.
   */
  reloadHorizontalScroll = () => {
    const { width, setWidth } = this.props;

    if (isEmpty(width) && setWidth) {
      setWidth(parseColumnsWidthsFromHeader(this.headerRef), () => {
        this.forceUpdate();
      });
    }

    if (this.scrollbarRef) {
      this.scrollbarRef.scrollLeft = this.scrollbarRef.scrollLeft > 0 ? 0 : 1;
    }
  };

  handleScroll = () => {
    this.forceUpdate();
  };

  showTable = () => {
    this.setState({ showTable: true });
  };

  setHeaderRef = (ref: HTMLElement) => {
    const { setRefForResize } = this.props;

    this.headerRef = ref;

    if (setRefForResize) {
      setRefForResize({ headerRef: ref });
    }
  };

  handleConfigurationChange = () => {
    const { setWidth } = this.props;

    if (setWidth) {
      setWidth(undefined, () => {
        this.forceUpdate();
      });
    }
  };

  render() {
    const { tableHeight, showTable } = this.state;
    const {
      items,
      totalItems,
      ui,
      configuration,
      isFetch,
      tableName,
      resizableColumns,
      customClass,
      headerRender,
      noContentText,
      outerSort,
      minColumnsWidth,
      width,
      forbidResizeFor,
      renderMaxHeight,
      getTranslate,
      loadNextPage,
      onSort,
      updateWidth,
      setRefForResize,
      setWidth,
      getTableRef,
      rowHeight = TABLE_ROW_HEIGHT,
      headerHeight = TABLE_HEADER_HEIGHT,
    } = this.props;
    const fixedColumns = configuration.filter(({ fixed }) => fixed);
    const isColumnsFixed = fixedColumns.every(({ fixed }) => fixed);
    const fixedColumnsWidth = fixedColumns
      .map(({ id }) => width?.[id] || 0)
      .reduce((prev, current) => prev + current, 0);

    const wrapperMaxHeight =
      window.innerWidth <= 560 ? renderMaxHeight : undefined;

    return (
      <div
        id='ui-table'
        className={classNames('ui-table', customClass, {
          'ui-table_fixed': isColumnsFixed,
          'ui-table_resizable': resizableColumns,
          'ui-table_empty': items.length === 0,
        })}
        ref={(el) => {
          this.tableRef = el;
        }}>
        <div
          className='ui-table__inner'
          style={{
            height: tableHeight,
            minHeight: wrapperMaxHeight,
            maxHeight: wrapperMaxHeight,
            opacity: showTable ? 1 : 0,
          }}>
          <Animation>
            <SmartList
              getCustomContentScrollWidth={this.getCustomContentScrollWidth}
              tooltip={true}
              setWidth={setWidth}
              initialScroll={ui}
              list={items}
              totalCount={totalItems || 9999}
              loadNextPage={loadNextPage}
              fixedColumnWidth={fixedColumnsWidth}
              rowRenderer={this.renderRowWithProps}
              headerHeight={headerHeight}
              rowHeight={rowHeight}
              headerRender={
                this.setTopPositionToCustomHeader(headerRender) || (
                  <TableHeaders
                    forbidResizeFor={forbidResizeFor}
                    setHeaderRef={this.setHeaderRef}
                    minColumnsWidth={minColumnsWidth}
                    width={width}
                    resizableColumns={resizableColumns}
                    updateWidth={updateWidth}
                    tableHeight={tableHeight}
                    headerHeight={headerHeight}
                    tableName={tableName}
                    outerSort={outerSort}
                    onSort={onSort}
                  />
                )
              }
              onScroll={this.handleScroll}
              hideHorizontal={false}
              getRef={(el: HTMLElement) => {
                this.scrollbarRef = el;

                if (getTableRef) {
                  getTableRef(el);
                }

                if (setRefForResize) {
                  setRefForResize({ scrollBarRef: el });
                }
              }}
              contentId='ui-table-content'
              noContent={
                isFetch ? (
                  <div />
                ) : (
                  <NoContent
                    className='ui-table__no-content'
                    text={getTranslate(
                      noContentText || 'common.noMatches.text'
                    )}
                  />
                )
              }
              onReady={this.showTable}
            />
          </Animation>
        </div>
        {isFetch && <Loader />}
      </div>
    );
  }
}

const mapStateToProps = (
  state: RootState,
  props: OwnProps
): ConnectedProps => ({
  configuration: getConfigurationByName(state, props.tableName),
  ui: getUIByName(state, props.tableName),
});

export default connect(mapStateToProps)(addTranslation(DynamicTable));
